// src/components/tools/BuiltinToolPicker.js
//
// Attach a platform built-in tool (e.g. web_search) to one of the user's
// agents. Built-ins are implemented on the backend, so there is nothing to
// configure: pick the agent, pick the tool, link it.

import React, { useEffect, useState } from 'react';
import { Link2 } from 'react-feather';
import { API_BASE_URL } from '../../config/api';
import { isKnowledgeTool } from '../../utils/toolVisibility';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const BuiltinToolPicker = ({ onCancel, onLinked }) => {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [tools, setTools] = useState([]);
  const [toolId, setToolId] = useState('');
  const [loadingTools, setLoadingTools] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/agents`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Failed to load assistants');
        const data = await res.json();
        const list = data.assistants || [];
        setAgents(list);
        if (list.length > 0) setAgentId(String(list[0].id));
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!agentId) return;
    (async () => {
      setLoadingTools(true);
      setError(null);
      setToolId('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/${agentId}/tools`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Failed to load built-in tools');
        const data = await res.json();
        const builtins = data.filter((t) => !t.is_custom && !isKnowledgeTool(t));
        setTools(builtins);
        const firstUnlinked = builtins.find((t) => !t.is_linked);
        if (firstUnlinked) setToolId(String(firstUnlinked.id));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingTools(false);
      }
    })();
  }, [agentId]);

  const selectedTool = tools.find((t) => String(t.id) === toolId);

  const handleAttach = async () => {
    if (!selectedTool) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/${agentId}/tools/link`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ tool_id: selectedTool.id, tool_name: selectedTool.name }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Failed to attach tool');
      }
      const agent = agents.find((a) => String(a.id) === agentId);
      if (onLinked) onLinked(`${selectedTool.name} attached to ${agent?.name || 'assistant'}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form-section-card">
      <div className="card-title-row">
        <Link2 size={20} />
        <h3>Attach a Built-in Tool</h3>
      </div>

      {error && <div className="save-message error">{error}</div>}

      <div className="grid-container">
        <div className="input-group">
          <label htmlFor="builtin-agent">Assistant</label>
          <select id="builtin-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            {agents.length === 0 && <option value="">No assistants</option>}
            {agents.map((a) => (
              <option key={a.id} value={String(a.id)}>{a.name}</option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="builtin-tool">Built-in Tool</label>
          <select
            id="builtin-tool"
            value={toolId}
            onChange={(e) => setToolId(e.target.value)}
            disabled={loadingTools || tools.length === 0}
          >
            {loadingTools && <option value="">Loading...</option>}
            {!loadingTools && tools.length === 0 && <option value="">No built-in tools available</option>}
            {!loadingTools && tools.map((t) => (
              <option key={t.id} value={String(t.id)} disabled={t.is_linked}>
                {t.name}{t.is_linked ? ' (attached)' : ''}
              </option>
            ))}
          </select>
        </div>

        {selectedTool?.description && (
          <div className="input-group full-width">
            <label>What it does</label>
            <p className="form-subtitle" style={{ margin: 0 }}>{selectedTool.description}</p>
          </div>
        )}
      </div>

      <div className="form-actions-footer">
        <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className={`primary-button ${saving ? 'is-loading' : ''}`}
          onClick={handleAttach}
          disabled={!selectedTool || selectedTool.is_linked || saving}
        >
          Attach Tool
        </button>
      </div>
    </div>
  );
};

export default BuiltinToolPicker;
